import { useRef, useState } from 'react';
import { Dialog, Popover } from 'react-aria-components';
import { plural } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import InputNumber from '@/design_system/InputNumber';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconEdit from '@/icons/Edit.svg';
import { useUpdateManualRepairDueDays } from '@/models/article';
import { RequestWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import './RepairDuration.css';

const { block } = createBEMClasses('repair-duration-tooltip');

const RepairDurationTooltip = ({ defaultWorkflowValue }: { defaultWorkflowValue: number }) => {
  const { article } = useArticleContext();
  const { t } = useLingui();
  const [isInvalid, setIsInvalid] = useState(false);
  const [manualRepairDueDaysValue, setManualRepairDueDaysValue] = useState(
    article.manualRepairDueDays
  );

  const { mutateAsync: updateManualRepairDueDays } = useUpdateManualRepairDueDays({
    articleId: article.id,
  });

  const placeholder = t({
    id: 'repair-duration.days',
    message: plural(Number(defaultWorkflowValue), {
      one: '# day',
      other: '# days',
    }),
  });

  return (
    <Dialog
      className={block()}
      aria-label={t({
        id: 'repair-duration.popover',
        message: 'Set the estimated time needed to complete the repair (in working days).',
      })}
    >
      <Stack gap={16}>
        <Paragraph>
          <Trans id="repair-duration.popover">
            Set the estimated time needed to complete the repair (in working days).
          </Trans>
        </Paragraph>
        <InputNumber
          placeholder={placeholder}
          size="small"
          isInvalid={isInvalid}
          value={manualRepairDueDaysValue ?? NaN}
          onChange={async (value) => {
            if (!isNaN(value) && (!Number.isInteger(value) || value < 0)) {
              return setIsInvalid(true);
            }
            setManualRepairDueDaysValue(isNaN(value) ? null : value);
            setIsInvalid(false);

            try {
              await updateManualRepairDueDays({
                manualRepairDueDays: isNaN(value) ? null : value,
              });
            } catch {
              setManualRepairDueDaysValue(article.manualRepairDueDays);
            }
          }}
        />
      </Stack>
    </Dialog>
  );
};

export const RepairDuration = () => {
  const popoverAnchorRef = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);
  const { request, workflow } = useRequestContext();
  const { article } = useArticleContext();
  const { t } = useLingui();
  const { isMobile } = useViewPort();

  const repairStep = workflow?.nodes.find((node) => 'step' in node && node.step === 'repair');

  const getDefaultWorkflowValue = (request: RequestWithRelations) => {
    const dueAtType = request?.dueAtType;

    return repairStep?.config?.statusDueAt?.repair.find(({ type }) => type === dueAtType)?.days;
  };

  const defaultWorkflowValue = getDefaultWorkflowValue(request);

  return (
    <Stack row={isMobile} alignItems={isMobile ? 'center' : 'end'} justifyContent="space-between">
      <Paragraph size="200" color="secondary">
        <Trans id="repair-duration.label">Repair duration (est.)</Trans>
      </Paragraph>
      <Stack row justifyContent={'end'} gap={8} ref={popoverAnchorRef}>
        <Paragraph>
          <Plural
            id="repair-duration.days"
            value={article.manualRepairDueDays ?? defaultWorkflowValue!}
            one="# day"
            other="# days"
          />
        </Paragraph>
        <Button
          ariaLabel={t({ id: 'repair-duration.buttonLabel', message: 'Edit repair duration' })}
          iconOnly
          size="small"
          variant="secondary"
          onPress={() => setOpen(true)}
        >
          <IconEdit />
        </Button>

        <Popover
          placement="bottom left"
          triggerRef={popoverAnchorRef}
          isOpen={isOpen}
          onOpenChange={setOpen}
          offset={4}
        >
          <RepairDurationTooltip defaultWorkflowValue={defaultWorkflowValue!} />
        </Popover>
      </Stack>
    </Stack>
  );
};
